import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

export default function Contact() {
  const [isVisible, setIsVisible] = useState<Record<string, boolean>>({});
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const observerRefs = useRef<Map<string, HTMLElement>>(new Map());

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.getAttribute("data-animate-id");
          if (id && entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [id]: true }));
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );
    observerRefs.current.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const setRef = (id: string) => (el: HTMLElement | null) => {
    if (el) observerRefs.current.set(id, el);
  };

  const animateClass = (id: string, base = "") =>
    `transition-all duration-700 ease-out ${base} ${
      isVisible[id] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
    }`;

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus("submitting");
    const form = e.currentTarget;
    const formData = new FormData(form);
    const data = new URLSearchParams();
    formData.forEach((value, key) => {
      data.append(key, value as string);
    });

    fetch(form.action, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: data.toString(),
    })
      .then(() => setFormStatus("success"))
      .catch(() => setFormStatus("error"));
  };

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative h-[320px] md:h-[400px] flex items-center justify-center overflow-hidden bg-brand-green-800">
        <div className="absolute inset-0 opacity-15">
          <img
            src="https://readdy.ai/api/search-image?query=Soft abstract green and beige organic shapes pattern, warm neutral tones, calming professional atmosphere, minimal elegant design, no text, gentle gradients"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 text-center px-4 max-w-3xl mx-auto">
          <span className="text-xs font-semibold uppercase tracking-widest text-brand-green-300 mb-3 block">
            Reach Out
          </span>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">
            Contact Us
          </h1>
          <p className="text-sm md:text-base text-brand-green-200 max-w-2xl mx-auto leading-relaxed">
            We are here to help. Whether you need care, have questions, or want to join our team, reach out anytime.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-10 md:py-14 bg-white border-b border-gray-100">
        <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {[
              {
                icon: "ri-map-pin-line",
                title: "Address",
                lines: ["4055 S Route 59, Suite 121", "Naperville, Illinois 60504"],
              },
              {
                icon: "ri-phone-line",
                title: "Phone",
                lines: ["773-699-7894 (24/7)", "773-669-7894", "773-310-2645"],
              },
              {
                icon: "ri-mail-line",
                title: "Email",
                lines: ["info@sycamoreoasis.com", "careers@sycamoreoasis.com"],
              },
              {
                icon: "ri-time-line",
                title: "Office Hours",
                lines: ["Monday – Friday: 9:00 AM – 5:00 PM", "With weekend and 24/7 On-call Manager"],
              },
            ].map((item) => (
              <div
                key={item.title}
                className="bg-soft-gray rounded-xl p-5 border border-gray-100 hover:border-brand-green-200 transition-all"
              >
                <div className="w-10 h-10 rounded-lg bg-brand-green-50 flex items-center justify-center mb-3">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${item.icon} text-brand-green-600`} />
                  </div>
                </div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">{item.title}</h3>
                {item.lines.map((line) => (
                  <p key={line} className="text-xs text-gray-500">{line}</p>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Common Questions Preview */}
      <section className="py-10 md:py-14 bg-white">
        <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
          <div className="text-center mb-6 md:mb-8">
            <span className="text-xs font-semibold uppercase tracking-widest text-brand-green-600 mb-2 block">
              Before You Reach Out
            </span>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
              Common Questions
            </h2>
            <p className="text-sm text-gray-500">
              Quick answers to what families ask most
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
            {[
              {
                q: "How quickly can care begin?",
                a: "Often within 48 hours of your free in-home assessment. Urgent needs can usually be accommodated same-day.",
              },
              {
                q: "Are your caregivers employees?",
                a: "Yes. Every caregiver is a W-2 employee, background-checked, insured, and receives ongoing training.",
              },
              {
                q: "Is there a minimum visit length?",
                a: "Our typical minimum is 3 hours per visit, but we tailor schedules to fit your family's needs and budget.",
              },
            ].map((item) => (
              <div
                key={item.q}
                className="bg-soft-gray rounded-xl p-5 md:p-6 border border-gray-100 hover:border-brand-green-200 transition-all"
              >
                <h3 className="text-sm font-semibold text-gray-900 mb-2">
                  {item.q}
                </h3>
                <p className="text-xs text-gray-500 leading-relaxed">
                  {item.a}
                </p>
              </div>
            ))}
          </div>
          <div className="text-center mt-6">
            <Link
              to="/faq"
              className="inline-flex items-center gap-2 text-sm font-medium text-brand-green-600 hover:text-brand-green-700 transition-colors"
            >
              See all FAQs
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-xs" />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Form + Map */}
      <section className="py-14 md:py-20 bg-soft-gray" data-animate-id="contact-form" ref={setRef("contact-form")}>
        <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Form */}
            <div className={animateClass("contact-form")}>
              <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100">
                <h2 className="text-lg font-semibold text-gray-900 mb-1">Send Us a Message</h2>
                <p className="text-sm text-gray-500 mb-6">
                  Fill out the form below and we will get back to you within one business day.
                </p>

                {/* What to Expect */}
                <div className="bg-brand-green-50 rounded-xl p-4 mb-6 border border-brand-green-100">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-brand-green-700 mb-3 flex items-center gap-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-information-line text-brand-green-600" />
                    </div>
                    What to Expect
                  </h3>
                  <div className="space-y-2.5">
                    {[
                      { step: "1", text: "We respond within 1 business day to schedule your call." },
                      { step: "2", text: "A care advisor visits your home for a free, no-obligation assessment." },
                      { step: "3", text: "You receive a personalized care plan and caregiver match within 48 hours." },
                    ].map((item) => (
                      <div key={item.step} className="flex items-start gap-2.5">
                        <span className="w-5 h-5 rounded-full bg-brand-green-200 text-brand-green-800 text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                          {item.step}
                        </span>
                        <p className="text-xs text-gray-600 leading-relaxed">{item.text}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {formStatus === "success" ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 rounded-full bg-brand-green-100 flex items-center justify-center mx-auto mb-4">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-check-line text-2xl text-brand-green-600" />
                      </div>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Message Sent!</h3>
                    <p className="text-sm text-gray-500 mb-4">
                      Thank you for reaching out. A member of our team will contact you shortly.
                    </p>
                    <button
                      onClick={() => setFormStatus("idle")}
                      className="px-6 py-2.5 bg-brand-green-600 text-white font-semibold text-sm rounded-full hover:bg-brand-green-700 transition-all"
                    >
                      Send Another Message
                    </button>
                  </div>
                ) : (
                  <form
                    action="https://readdy.ai/api/form/d7r764kcmq04e0g4e4c0"
                    method="POST"
                    data-readdy-form
                    id="contact-page-form"
                    onSubmit={handleSubmit}
                    className="space-y-4"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="firstName" className="block text-xs font-medium text-gray-700 mb-1.5">
                          First Name *
                        </label>
                        <input
                          type="text"
                          id="firstName"
                          name="firstName"
                          required
                          className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none transition-all"
                          placeholder="John"
                        />
                      </div>
                      <div>
                        <label htmlFor="lastName" className="block text-xs font-medium text-gray-700 mb-1.5">
                          Last Name *
                        </label>
                        <input
                          type="text"
                          id="lastName"
                          name="lastName"
                          required
                          className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none transition-all"
                          placeholder="Doe"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-xs font-medium text-gray-700 mb-1.5">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none transition-all"
                        placeholder="john@example.com"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-xs font-medium text-gray-700 mb-1.5">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none transition-all"
                        placeholder="773-699-7894"
                      />
                    </div>

                    <div>
                      <label htmlFor="subject" className="block text-xs font-medium text-gray-700 mb-1.5">
                        Subject *
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        required
                        className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none transition-all bg-white"
                      >
                        <option value="">Select a subject...</option>
                        <option value="care-inquiry">Care Inquiry</option>
                        <option value="care-plan">Care Plan Questions</option>
                        <option value="service-areas">Service Area Question</option>
                        <option value="careers">Career / Hiring</option>
                        <option value="feedback">Feedback</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-xs font-medium text-gray-700 mb-1.5">
                        Message *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows={5}
                        maxLength={500}
                        required
                        className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none transition-all resize-none"
                        placeholder="How can we help you today?"
                      />
                      <p className="text-[11px] text-gray-400 mt-1">Maximum 500 characters</p>
                    </div>

                    <button
                      type="submit"
                      disabled={formStatus === "submitting"}
                      className="w-full px-6 py-3 bg-brand-green-600 hover:bg-brand-green-700 disabled:bg-brand-green-400 text-white font-semibold rounded-lg transition-all duration-200 whitespace-nowrap"
                    >
                      {formStatus === "submitting" ? "Sending..." : "Send Message"}
                    </button>

                    {formStatus === "error" && (
                      <p className="text-sm text-red-600 text-center">
                        Something went wrong. Please try again or call us directly.
                      </p>
                    )}
                  </form>
                )}
              </div>
            </div>

            {/* Map + Emergency */}
            <div className={animateClass("contact-form")}>
              <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 mb-6">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d190601.123456789!2d-88.2!3d41.75!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x880e54c7d0e5e0e1%3A0x1234567890abcdef!2sNaperville%2C%20IL!5e0!3m2!1sen!2sus!4v1600000000000!5m2!1sen!2sus"
                  width="100%"
                  height="350"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Sycamore Oasis Location"
                  className="w-full"
                />
              </div>

              <div className="bg-red-50 rounded-xl p-5 border border-red-100">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center shrink-0">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-alarm-warning-line text-red-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-red-800 mb-1">Need Immediate Help?</h3>
                    <p className="text-sm text-red-700 mb-3">
                      For emergencies, please call 911. For urgent care needs or after-hours support, our on-call team is available 24/7.
                    </p>
                    <a
                      href="tel:+17736997894"
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-600 text-white font-semibold text-sm rounded-full hover:bg-red-700 transition-all whitespace-nowrap"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-phone-line text-xs" />
                      </div>
                      Call 773-699-7894 (24/7)
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-20 bg-brand-green-800">
        <div className="w-full px-4 md:px-8 max-w-4xl mx-auto text-center">
          <div className="w-12 h-12 rounded-full bg-brand-green-700 flex items-center justify-center mx-auto mb-4">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-heart-3-line text-2xl text-brand-green-300" />
            </div>
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
            Let Us Help You Live Better
          </h2>
          <p className="text-sm md:text-base text-brand-green-200 mb-8 max-w-2xl mx-auto leading-relaxed">
            Every conversation starts with listening. Reach out today and let us understand how we can support you and your loved ones.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
            <Link
              to="/services"
              className="w-full sm:w-auto px-8 py-3.5 bg-white text-brand-green-800 font-semibold rounded-full hover:bg-brand-green-50 transition-all duration-200 text-center whitespace-nowrap"
            >
              Explore Our Services
            </Link>
            <Link
              to="/care-plans"
              className="w-full sm:w-auto px-8 py-3.5 bg-brand-green-700 text-white font-semibold rounded-full hover:bg-brand-green-600 transition-all duration-200 text-center whitespace-nowrap"
            >
              View Care Plans
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}